import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { trackPageView, trackScrollDepth, trackSectionView } from '../lib/analytics'

/** Fires a GA4 page_view on every client-side route change. */
export const usePageViews = () => {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location.pathname, location.search])
}

/** Fires scroll_depth at 25/50/75/100%, once each per mount. */
export const useScrollDepth = () => {
  const fired = useRef<Set<number>>(new Set())
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const pct = (window.scrollY / scrollable) * 100
      for (const mark of [25, 50, 75, 100] as const) {
        if (pct >= mark && !fired.current.has(mark)) {
          fired.current.add(mark)
          trackScrollDepth(mark)
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
}

/** Fires section_view once when each section first becomes visible. */
export const useSectionViews = (sectionIds: readonly string[]) => {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const seen = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !seen.has(entry.target.id)) {
            seen.add(entry.target.id)
            trackSectionView(entry.target.id)
          }
        }
      },
      { threshold: 0.35 },
    )
    for (const id of sectionIds) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [sectionIds])
}
